import { useMemo, type MouseEvent } from 'react';
import { Flex, Progress, Tooltip, Typography } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import type { ColumnsType } from 'antd/es/table';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { TeamBadge } from '@/components';
import {
  BellIcon,
  DotsIcon,
  DownArrowIcon,
  SubsetIcon,
  UpArrowIcon,
} from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { useUnAuthorize } from '@/hooks';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import type {
  InventoryItemRow,
  InventorySortDirection,
  InventorySortKey,
} from '../type';
import type { FlatRow } from './useInventoryTable';
import { getStatusClassName } from './useInventoryTable';
import InventoryCellTooltip from '../components/InventoryCellTooltip';
import OverflowTooltipText from '../components/OverflowTooltipText';
import { createSpecialRowRenderers } from '../components/inventoryTableSpecialRows';
import { handleDownloadSKU } from '../components/InventoryDetailDrawer';

type UseInventoryColumnsArgs = {
  handleDeleteClick: (record: InventoryItemRow) => void;
  handleEditInventory?: (
    isOpen?: boolean,
    name?: string,
    id?: string,
    isDuplicate?: boolean
  ) => void;
  handleRowClick: (row: FlatRow) => Promise<void>;
  isFlatMode: boolean;
  isMobileTableScroll: boolean;
  isPerDeviceView: boolean;
  isScrollMode: boolean;
  isWideScreen: boolean;
  loadingRowKey: string | null;
  onSortChange?: (
    key: InventorySortKey,
    direction: InventorySortDirection
  ) => void;
  sortDirection?: InventorySortDirection;
  sortKey?: InventorySortKey;
  tableColumnCount: number;
  toggleGroup: (groupId: string) => void;
  toggleSubGroup: (groupId: string, subgroupId: string) => void;
};

const MENU_ITEMS = [
  { key: 'setup_notification', label: 'Setup notification' },
  { key: 'edit', label: 'Edit' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'download', label: 'Download' },
  { key: 'delete', label: 'Delete' },
] as const;

const compactMetricValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';

  const str = String(value);

  if (str.length <= 3) return str;

  return `${str.slice(-3)}...`;
};

const useInventoryColumns = ({
  handleDeleteClick,
  handleEditInventory,
  handleRowClick,
  isFlatMode,
  isMobileTableScroll,
  isPerDeviceView,
  isScrollMode,
  isWideScreen,
  loadingRowKey,
  onSortChange,
  sortDirection = 'asc',
  sortKey,
  tableColumnCount,
  toggleGroup,
  toggleSubGroup,
}: UseInventoryColumnsArgs): ColumnsType<FlatRow> => {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const { formatDateTime } = useUserDateTimeFormat();

  return useMemo(() => {
    const inventoryTeamTruncateLength = isMobileTableScroll
      ? 7
      : isWideScreen
        ? 20
        : isScrollMode
          ? 8
          : 15;

    const responsiveColumnWidth = (
      wideWidth: number,
      compactWidth: string,
      mobileWidth = wideWidth
    ) => {
      if (isMobileTableScroll) return mobileWidth;
      if (isScrollMode) return compactWidth;
      return wideWidth;
    };

    const renderColumnTitle = (
      title: string,
      columnSortKey?: InventorySortKey
    ) => {
      if (!columnSortKey || !onSortChange) {
        return (
          <Typography.Text
            ellipsis={isScrollMode ? { tooltip: title } : false}
            className='inventory-table-column-title'
          >
            {title}
          </Typography.Text>
        );
      }

      const isActive = sortKey === columnSortKey;
      const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (isActive) {
          onSortChange(columnSortKey, sortDirection === 'asc' ? 'desc' : 'asc');
          return;
        }
        onSortChange(columnSortKey, 'asc');
      };

      return (
        <button
          type='button'
          className={`inventory-table-sortable-title${
            isActive ? ' is-active' : ''
          }`}
          onClick={handleClick}
          aria-label={`Sort by ${title}${
            isActive
              ? ` (${sortDirection === 'asc' ? 'ascending' : 'descending'})`
              : ''
          }`}
        >
          <Typography.Text
            ellipsis={isScrollMode ? { tooltip: title } : false}
            className='inventory-table-column-title-text'
          >
            {title}
          </Typography.Text>
          {isActive ? (
            sortDirection === 'asc' ? (
              <UpArrowIcon
                size={12}
                color='var(--text-light)'
                style={{ marginRight: 0 }}
              />
            ) : (
              <DownArrowIcon
                size={12}
                color='var(--text-light)'
                style={{ marginRight: 0 }}
              />
            )
          ) : null}
        </button>
      );
    };

    const handleMenuClick = (key: string, record: InventoryItemRow) => {
      const resolvedId = record.inventoryId;
      switch (key) {
        case 'setup_notification':
          handleEditInventory?.(true, 'setup_notification', resolvedId, false);
          break;
        case 'edit':
          handleEditInventory?.(true, 'inventory_form', resolvedId, false);
          break;
        case 'duplicate':
          handleEditInventory?.(true, 'inventory_form', resolvedId, true);
          break;
        case 'delete':
          handleDeleteClick(record);
          break;
        case 'download':
          handleDownloadSKU(record?.id, token, unauthorize);
          break;
        default:
          break;
      }
    };

    const { renderBlankSpecialCell, renderFullWidthSpecialRow } =
      createSpecialRowRenderers({
        tableColumnCount,
        loadingRowKey,
        handleRowClick,
        toggleGroup,
        toggleSubGroup,
      });

    if (isPerDeviceView) {
      return [
        {
          title: renderColumnTitle('Device ID', 'device_id'),
          dataIndex: 'deviceId',
          key: 'deviceId',
          width: responsiveColumnWidth(150, '12%', 150),
          render: (_, record) => {
            const guard = renderFullWidthSpecialRow(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;
            const itemDepthClass =
              item.__depth >= 2
                ? 'depth-2'
                : item.__depth === 1
                  ? 'depth-1'
                  : 'depth-0';

            return (
              <Flex
                align='center'
                className={`inventory-device-id-cell ${itemDepthClass}`}
              >
                <OverflowTooltipText
                  title={item.deviceId}
                  className='inventory-cell-text'
                >
                  {item.deviceId}
                </OverflowTooltipText>
              </Flex>
            );
          },
        },
        {
          title: renderColumnTitle('Site ID', 'location'),
          dataIndex: 'siteId',
          key: 'siteId',
          width: responsiveColumnWidth(130, '10%', 130),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;

            return (
              <OverflowTooltipText
                title={item.siteName}
                className='inventory-cell-text'
              >
                {item.siteName}
              </OverflowTooltipText>
            );
          },
        },
        {
          title: renderColumnTitle('SKU', 'sku'),
          dataIndex: 'sku',
          key: 'sku',
          width: responsiveColumnWidth(130, '10%', 130),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;

            return (
              <OverflowTooltipText
                title={item.sku}
                className='inventory-cell-text'
              >
                {item.sku}
              </OverflowTooltipText>
            );
          },
        },
        {
          title: renderColumnTitle('Status', 'status'),
          dataIndex: 'deviceStatus',
          key: 'deviceStatus',
          width: responsiveColumnWidth(130, '10%', 130),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;

            return item.deviceStatus ? (
              <span
                className={`inventory-device-status-pill inventory-device-status-pill--${item.deviceStatus.toLowerCase()}`}
              >
                {item.deviceStatus}
              </span>
            ) : (
              <span>--</span>
            );
          },
        },
        {
          title: renderColumnTitle(themeT('team', theme.themeType), 'team'),
          dataIndex: 'teams',
          key: 'teams',
          width: responsiveColumnWidth(160, '13%', 160),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;

            if (!item.teams?.length) return <span>--</span>;

            return (
              <TeamBadge
                truncateLength={inventoryTeamTruncateLength}
                color={item.teams[0]?.color}
                text={item.teams[0]?.name}
                tooltipText={item.teams[0]?.name}
              />
            );
          },
        },
        {
          title: renderColumnTitle('Shipped in', 'shipped_in'),
          dataIndex: 'shippedIn',
          key: 'shippedIn',
          width: responsiveColumnWidth(140, '11%', 140),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;
            return (
              formatDateTime(item.shippedIn, { includeTime: false }) || '--'
            );
          },
        },
        {
          title: renderColumnTitle('Activated', 'activated'),
          dataIndex: 'activatedAt',
          key: 'activatedAt',
          width: responsiveColumnWidth(140, '11%', 140),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;
            return formatDateTime(item.activatedAt, { includeTime: false });
          },
        },
        {
          title: renderColumnTitle('Returned', 'returned'),
          dataIndex: 'returnedAt',
          key: 'returnedAt',
          width: responsiveColumnWidth(140, '11%', 140),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;
            return formatDateTime(item.returnedAt, { includeTime: false });
          },
        },
        {
          title: renderColumnTitle('Last updated', 'last_updated'),
          dataIndex: 'updatedAt',
          key: 'updatedAt',
          width: responsiveColumnWidth(160, '12%', 150),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            const item = record as InventoryItemRow;
            const updatedAt = formatDateTime(item.updatedAt) || '--';

            return (
              <OverflowTooltipText
                title={updatedAt}
                className='inventory-cell-text'
              >
                {updatedAt}
              </OverflowTooltipText>
            );
          },
        },
        {
          title: '',
          key: 'action',
          className: 'inventory-action-column',
          width: responsiveColumnWidth(52, '5%', 70),
          render: (_, record) => {
            const guard = renderBlankSpecialCell(record);
            if (guard) return guard;
            return null;
          },
        },
      ];
    }

    return [
      {
        title: renderColumnTitle('SKU'),
        dataIndex: 'sku',
        key: 'sku',
        width: isWideScreen ? 130 : responsiveColumnWidth(150, '12%', 150),
        render: (_, record) => {
          const guard = renderFullWidthSpecialRow(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;

          const itemDepthClass =
            item.__depth >= 2
              ? 'depth-2'
              : item.__depth === 1
                ? 'depth-1'
                : 'depth-0';

          const showGroupedItemIcon = !isFlatMode && item.__depth > 0;

          const skuTooltipItems = item.skuTooltip?.length
            ? item.skuTooltip
            : [];

          return (
            <Flex
              align='center'
              className={`inventory-sku-cell ${itemDepthClass}`}
            >
              {showGroupedItemIcon && (
                <span className='inventory-grouped-item-icon'>
                  <SubsetIcon />
                </span>
              )}

              <OverflowTooltipText
                title={item.sku}
                className='inventory-cell-text'
              >
                {item.sku}
              </OverflowTooltipText>

              {item.skuTooltip?.length ? (
                <Tooltip
                  overlayClassName='inventory-tooltip'
                  title={<InventoryCellTooltip items={skuTooltipItems} />}
                >
                  <span
                    onClick={(event) => event.stopPropagation()}
                    className='inventory-subitem-count'
                  >
                    {item.subItemCount}
                  </span>
                </Tooltip>
              ) : null}
            </Flex>
          );
        },
      },
      {
        title: renderColumnTitle('Description'),
        dataIndex: 'description',
        key: 'description',
        width: isWideScreen ? 200 : responsiveColumnWidth(180, '17%', 230),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;

          return (
            <OverflowTooltipText
              title={item.description}
              className='inventory-cell-text'
            >
              {item.description}
            </OverflowTooltipText>
          );
        },
      },
      {
        title: renderColumnTitle('Location'),
        dataIndex: 'location',
        key: 'location',
        width: isWideScreen ? 180 : responsiveColumnWidth(140, '12%', 180),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;

          const locationItems = item.locationTooltip?.length
            ? item.locationTooltip
            : (item.locations || []).map((loc) => ({
                label: loc.name,
                value: '',
              }));

          const locationCount =
            item.locations?.length ||
            locationItems.length ||
            (item.location ? 1 : 0);

          const locationText =
            locationCount > 1
              ? `${locationCount} locations`
              : item.location || '--';

          if (!locationItems.length || locationCount <= 1) {
            return (
              <OverflowTooltipText
                title={locationText}
                className='inventory-cell-text'
              >
                {locationText}
              </OverflowTooltipText>
            );
          }

          const locationNode = (
            <Typography.Text ellipsis className='inventory-cell-text'>
              {locationText}
            </Typography.Text>
          );

          return (
            <Tooltip
              overlayClassName='inventory-tooltip'
              title={<InventoryCellTooltip items={locationItems} />}
            >
              <span
                onClick={(event) => event.stopPropagation()}
                className='inventory-location-tooltip-trigger'
              >
                {locationNode}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: renderColumnTitle(themeT('team', theme.themeType)),
        dataIndex: 'teams',
        key: 'teams',
        width: responsiveColumnWidth(180, '15%', 170),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;

          if (!item.teams?.length) {
            return <span>--</span>;
          }

          return (
            <div className='inventory-team-cell'>
              <TeamBadge
                truncateLength={inventoryTeamTruncateLength}
                color={item.teams[0]?.color}
                text={item.teams[0]?.name}
                tooltipText={item.teams[0]?.name}
              />

              {item.teams.length > 1 && (
                <Tooltip
                  title={item.teams
                    .slice(1)
                    .map((team) => team.name)
                    .join(', ')}
                  placement='top'
                >
                  <span className='inventory-team-extra'>
                    <TeamBadge
                      color={null}
                      text={`+ ${item.teams.length - 1}`}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: renderColumnTitle('Stock vs required'),
        key: 'stock-vs-required',
        width: responsiveColumnWidth(150, '15%', 170),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;

          const content = (
            <Flex
              align='center'
              gap={8}
              onClick={(event) => event.stopPropagation()}
              className='inventory-metric-cell'
            >
              <Typography.Text ellipsis className='inventory-metric-value'>
                {compactMetricValue(item.stock)} /
                <span
                  className={
                    item.required
                      ? 'inventory-metric-value-target'
                      : 'inventory-metric-value-target is-empty'
                  }
                >
                  {compactMetricValue(item.required)}
                </span>
              </Typography.Text>

              {item.status ? (
                <span className={getStatusClassName(item.status)}>
                  {item.status}
                </span>
              ) : null}
            </Flex>
          );

          if (!item.stockVsRequiredTooltip?.length) {
            return content;
          }

          return (
            <Tooltip
              overlayClassName='inventory-tooltip'
              title={
                <InventoryCellTooltip items={item.stockVsRequiredTooltip} />
              }
            >
              {content}
            </Tooltip>
          );
        },
      },
      {
        title: renderColumnTitle('Stock planning'),
        key: 'stock-planning',
        width: responsiveColumnWidth(170, '15%', 190),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;

          const percent =
            item.stockPlanningTotal > 0
              ? Math.round(
                  (item.stockPlanningCurrent / item.stockPlanningTotal) * 100
                )
              : 0;

          const content = (
            <Flex
              align='center'
              gap={8}
              onClick={(event) => event.stopPropagation()}
              className='inventory-metric-cell'
            >
              <Typography.Text ellipsis className='inventory-metric-value'>
                {compactMetricValue(item.stockPlanningCurrent)} /
                <span
                  className={
                    item.stockPlanningTotal
                      ? 'inventory-metric-value-target'
                      : 'inventory-metric-value-target is-empty'
                  }
                >
                  {compactMetricValue(item.stockPlanningTotal)}
                </span>
              </Typography.Text>

              <Progress
                percent={percent}
                showInfo={false}
                size={[56, 6]}
                strokeColor='#717171'
                trailColor='#F3F3F3'
              />
            </Flex>
          );

          if (!item.stockPlanningTooltip?.length) {
            return content;
          }

          return (
            <Tooltip
              overlayClassName='inventory-tooltip'
              title={<InventoryCellTooltip items={item.stockPlanningTooltip} />}
            >
              {content}
            </Tooltip>
          );
        },
      },
      {
        title: renderColumnTitle('Last updated'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: responsiveColumnWidth(170, '13%', 150),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const item = record as InventoryItemRow;
          const updatedAt = formatDateTime(item.updatedAt) || '--';

          return (
            <OverflowTooltipText
              title={updatedAt}
              className='inventory-cell-text'
            >
              {updatedAt}
            </OverflowTooltipText>
          );
        },
      },
      {
        title: '',
        key: 'action',
        className: 'inventory-action-column',
        width: responsiveColumnWidth(72, '7%', 90),
        render: (_, record) => {
          const guard = renderBlankSpecialCell(record);
          if (guard) return guard;
          const inventoryItem = record as InventoryItemRow;
          if (!inventoryItem.sku) return null;

          return (
            <Flex
              align='center'
              justify='end'
              gap={10}
              className='inventory-action-icons'
            >
              {inventoryItem.hasNotification && (
                <span
                  className='inventory-bell-icon-wrapper'
                  onClick={(event) => event.stopPropagation()}
                >
                  <BellIcon color='var(--light-blue)' width={16} height={16} />
                </span>
              )}

              <Dropdown
                trigger={['hover']}
                placement='bottomRight'
                dropdownRender={() => (
                  <div className='inventory-custom-dropdown-menu'>
                    {MENU_ITEMS.map((item) => {
                      if (
                        item.key === 'setup_notification' &&
                        !inventoryItem.can_save_notification
                      ) {
                        return null;
                      }

                      return (
                        <div
                          key={item.key}
                          className='dropdown-item'
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMenuClick(item.key, inventoryItem);
                          }}
                        >
                          <span className='header-menu-item-content'>
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              >
                <span
                  className='dots-icon-wrapper'
                  onClick={(event) => event.stopPropagation()}
                >
                  <DotsIcon
                    color='var(--text-primary)'
                    width={14}
                    height={14}
                  />
                </span>
              </Dropdown>
            </Flex>
          );
        },
      },
    ];
  }, [
    handleDeleteClick,
    handleEditInventory,
    handleRowClick,
    isPerDeviceView,
    isFlatMode,
    isMobileTableScroll,
    isScrollMode,
    isWideScreen,
    loadingRowKey,
    onSortChange,
    sortDirection,
    sortKey,
    tableColumnCount,
    toggleGroup,
    toggleSubGroup,
    formatDateTime,
    token,
    unauthorize,
  ]);
};

export default useInventoryColumns;
